/**
 * archiveEnableContainer.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import * as React from "react";

import { Alert, AlertTitle, Button } from "@mui/material";

import { archiveServiceFactory } from "../services/archive.service";
import { authServiceFactory } from "../services/auth.service";
import { cryptoServiceFactory } from "../services/crypto.service";

interface Props {
  accountID: string,
  onFinish(kyberKey: string): void
}
export function ArchiveEnableContainer(props: Props): React.ReactElement {
  const [saving, setSaving] = React.useState<boolean>(false);
  const [errMsg, setErrMsg] = React.useState<string>("");
  const [kyberKey, setKyberKey] = React.useState<string>("");
  const [copied, setCopied] = React.useState<boolean>(false);
  const [displayKey, setDisplayKey] = React.useState<string>("");

  async function copyToClipboard() {
    try {
      await navigator.clipboard.writeText(displayKey);
      setCopied(true);
    } catch (err) {
      console.error("Error copying to clipboard:", err);
      setErrMsg("Couldn't copy to clipboard");
    }
  }

  async function createArchiverKeys() {
    try {
      if (saving) { return; }
      setSaving(true);
      const cryptoService = cryptoServiceFactory();
      const archiveService = archiveServiceFactory();
      const keys = cryptoService.generateKyberKeyPair();
      const pubKey = keys.publicKey.exportKeyToBase64();
      const priKey = keys.privateKey.exportKeyToBase64();

      const dispKey = archiveService.kyberPemFormat(priKey);
      await authServiceFactory().updateAccountInfo(props.accountID, {kyberKey: pubKey});
      setDisplayKey(dispKey);
      setKyberKey(pubKey);
      keys.delete();
    } catch (err) {
      console.error("Error creating archiver keys", err);
      setErrMsg((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return <div className="archiving-disabled-container">
    { errMsg && <Alert severity="error" onClick={() => setErrMsg("")}>
      <AlertTitle>Error</AlertTitle>
      <p>{errMsg}</p>
    </Alert> }

    { displayKey ? <div className="display-priv-key-container">
        <h2>Archiving: Enabled</h2>
        <div className="form-wrapper">
        <Alert severity="warning">
          <AlertTitle>Warning</AlertTitle>
          <p>Copy the private key below and save it in a secure location.
          It will be deleted as soon as you close this dialog, and this is the only
          time you will see it!</p>
          <p><strong>If you lose it, you will be unable to access your archived data!</strong></p>
        </Alert>
        <div className="button-row">
          <Button variant="contained" color={copied ? "secondary" : "primary"}
              disableRipple disableTouchRipple
              aria-label="clipboard-copy"
              onClick={copyToClipboard}>
            <i className="fa-solid fa-copy" />
          </Button>
        </div>
        <div className="private-key-container">
          <pre>{displayKey}</pre>
        </div>
        <div className="button-row">
          <Button onClick={() => props.onFinish(kyberKey)}
              disableRipple disableTouchRipple
              color="primary">
            Finish
          </Button>
        </div>
      </div>
    </div> : <>
      <h2>Archiving: Disabled</h2>
      <p className="disabled-desc">Archiving is currently disabled for your account.</p>
      <Alert severity="info">
        <p className="">To enable archiving, be prepared to copy and save a master
        private key, whose counterpart will encrypt your archive.</p>
        <p>Click below to get started.</p>
      </Alert>
      <p></p>
      <div className="button-row">
        <Button variant="contained"
            disableRipple disableTouchRipple
            onClick={createArchiverKeys}>Enable</Button>
      </div>
    </>}
  </div>;
}
